import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"
import { buttonTypography } from "../../styles/typography"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md font-[var(--font-weight-medium)] ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 font-[var(--font-family-primary)]",
  {
    variants: {
      variant: {
        // Amazon Design System Primary - Orange
        default: "bg-[var(--amazon-orange)] text-white hover:bg-[var(--amazon-orange-hover)] focus:ring-[var(--amazon-orange)]/20",
        // Amazon Design System Secondary - Dark Blue
        secondary: "bg-[var(--amazon-dark-blue)] text-white hover:bg-[var(--amazon-dark-blue)]/90 focus:ring-[var(--amazon-dark-blue)]/20",
        // Destructive - Error state
        destructive: "bg-[var(--error-color)] text-white hover:bg-[var(--error-color)]/90 focus:ring-[var(--error-color)]/20",
        // Outline - Amazon Orange border
        outline: "border border-[var(--amazon-orange)] bg-white text-[var(--amazon-orange)] hover:bg-[var(--amazon-orange)] hover:text-white focus:ring-[var(--amazon-orange)]/20",
        // Ghost - Transparent background
        ghost: "text-[var(--amazon-dark-blue)] hover:bg-[var(--amazon-orange)]/10 focus:ring-[var(--amazon-orange)]/20",
        // Link - Text only
        link: "text-[var(--amazon-orange)] underline-offset-4 hover:underline focus:ring-[var(--amazon-orange)]/20",
      },
      size: {
        default: "h-10 px-4 py-2 text-[var(--font-size-sm)]",
    sm: "h-9 rounded-md px-3 text-[var(--font-size-xs)]",
    lg: "h-11 rounded-md px-8 text-[var(--font-size-base)]",
    icon: "h-10 w-10 text-[var(--font-size-sm)]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    
    // Get typography based on size
    const getTypographyForSize = (buttonSize: string | null | undefined = 'default') => {
      switch (buttonSize) {
        case 'sm':
          return buttonTypography.small
        case 'lg':
          return buttonTypography.large
        default:
          return buttonTypography.primary
      }
    }
    
    const typography = getTypographyForSize(size)
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        style={{
          fontWeight: typography.fontWeight,
          lineHeight: typography.lineHeight,
          letterSpacing: typography.letterSpacing,
          ...props.style
        }}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }